/**
 * What the user reads when the server refuses a Kanban card move. Free of Vue so it can be unit-tested.
 */

/** Shown when the server gives no words a user can read. */
export const MOVE_REFUSED_FALLBACK = 'The task could not be moved.';

const SERVER_ERROR_STATUS = 500;
const UNAUTHORIZED_STATUS = 401;
const CANCELED_CODE = 'ERR_CANCELED';
const CANCELED_NAME = 'CanceledError';

/** A non-empty string that is words, not an HTML error page. */
function readable(text) {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  return trimmed && !trimmed.startsWith('<') ? trimmed : null;
}

/**
 * The one message for a refused move, or null when another layer owns it.
 * - 401 or a cancelled request: null. The HTTP layer refreshes the session or signs out.
 * - 4xx with the server's words: those words. A task rule refusal (`task.status_not_on_board`,
 *   `task.person_not_on_board`) has them in `message`; a missing permission (403) is plain text.
 *   Never `detail`: it holds the refused id.
 * - 5xx, no response, or no readable words: {@link MOVE_REFUSED_FALLBACK}.
 * @param {object} [err] the rejected axios error
 * @returns {string|null}
 */
export function moveRefusalMessage(err) {
  if (err?.name === CANCELED_NAME || err?.code === CANCELED_CODE) return null;
  const response = err?.response;
  if (!response) return MOVE_REFUSED_FALLBACK;
  if (response.status === UNAUTHORIZED_STATUS) return null;
  if (response.status >= SERVER_ERROR_STATUS) return MOVE_REFUSED_FALLBACK;

  const { data } = response;
  const words = typeof data === 'string' ? readable(data) : readable(data?.message);
  return words || MOVE_REFUSED_FALLBACK;
}
